import { Input } from "@/src/components/ui/input";
import {
  formatCardNumber,
  formatExpiryDate,
} from "@/src/lib/utils/card-formatting";
import type { UseFormReturn } from "react-hook-form";
import type { CheckoutFormData } from "@/src/lib/schemas/checkout-schemas";
import { cn } from "@/src/lib/utils";

const CARD_FIELD_IDS = {
  number: "checkout-card-number",
  expiry: "checkout-card-expiry",
  cvc: "checkout-card-cvc",
} as const;

interface PaymentFormProps {
  form: UseFormReturn<CheckoutFormData>;
  paymentError?: string | null;
  couponCode: string;
  onCouponCodeChange: (value: string) => void;
  onApplyCoupon: () => void | Promise<boolean>;
  onRemoveCoupon: () => void | Promise<boolean>;
  isApplyingCoupon: boolean;
  couponError?: string | null;
  appliedCouponLabel?: string | null;
  canRemoveCoupon?: boolean;
}

export function PaymentForm({
  form,
  paymentError,
  couponCode,
  onCouponCodeChange,
  onApplyCoupon,
  onRemoveCoupon,
  isApplyingCoupon,
  couponError,
  appliedCouponLabel,
  canRemoveCoupon,
}: PaymentFormProps) {
  const { watch, setValue, formState } = form;
  const errors = formState.errors;
  const paymentMethod = watch("paymentMethod") ?? "card";

  return (
    <div>
      <div className="mb-2 sm:mb-2.5 flex items-center gap-2 sm:gap-3">
        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-[26px] font-bold leading-tight sm:leading-[26px] text-[rgb(48,48,48)]">
          STEP 4: PAYMENT METHOD
        </h3>
      </div>
      <p className="mb-2 sm:mb-2.5 flex items-center gap-2 text-xs sm:text-sm text-[rgb(155,155,155)]">
        All transactions are secure and encrypted
      </p>

      {paymentError && (
        <div className="mb-3 sm:mb-4 rounded-lg border border-red-300 bg-red-50 p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-red-600">{paymentError}</p>
        </div>
      )}

      <div className="space-y-2">
        {/* Credit/Debit Card Option */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <label
            className={cn(
              "flex cursor-pointer items-center justify-between p-3 sm:p-4 md:p-5 transition-colors",
              paymentMethod === "card"
                ? "bg-gray-50"
                : "bg-white hover:bg-gray-50",
            )}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === "card"}
                onChange={() => setValue("paymentMethod", "card")}
                className="h-4 w-4 sm:h-5 sm:w-5"
              />

              <span className="text-sm font-medium sm:text-base">
                Credit/Debit Card
              </span>
            </div>
            <div className="flex gap-0.5 sm:gap-1">
              <img
                src="/brandnetwork/visa.svg"
                alt="Visa"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-[30px] md:h-[30px]"
              />
              <img
                src="/brandnetwork/mastercard.svg"
                alt="Mastercard"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-[30px] md:h-[30px]"
              />
              <img
                src="/brandnetwork/amex.svg"
                alt="Amex"
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-[30px] md:h-[30px]"
              />
            </div>
          </label>
          {paymentMethod === "card" && (
            <div className="space-y-3 sm:space-y-4 border-t border-gray-200 p-3 sm:p-4 md:p-5">
              <div>
                <label
                  htmlFor={CARD_FIELD_IDS.number}
                  className="mb-1.5 block text-xs font-medium text-[rgb(48,48,48)] sm:text-sm"
                >
                  Card number
                </label>
                <Input
                  id={CARD_FIELD_IDS.number}
                  value={watch("cardNumber")}
                  placeholder="1234 1234 1234 1234"
                  autoComplete="cc-number"
                  inputMode="numeric"
                  className={cn(
                    "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                    errors.cardNumber &&
                      "border-red-500 focus:border-red-500 focus:ring-red-100",
                  )}
                  maxLength={19}
                  onChange={(e) =>
                    setValue("cardNumber", formatCardNumber(e.target.value))
                  }
                  data-card-field="cardNumber"
                />
                {errors.cardNumber && (
                  <p
                    id={`${CARD_FIELD_IDS.number}-error`}
                    className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600"
                    role="alert"
                  >
                    {errors.cardNumber.message}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label
                    htmlFor={CARD_FIELD_IDS.expiry}
                    className="mb-1.5 block text-xs font-medium text-[rgb(48,48,48)] sm:text-sm"
                  >
                    Expiry (MM/YY)
                  </label>
                  <Input
                    id={CARD_FIELD_IDS.expiry}
                    value={watch("expiryDate")}
                    placeholder="MM/YY*"
                    autoComplete="cc-exp"
                    inputMode="numeric"
                    className={cn(
                      "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                      errors.expiryDate &&
                        "border-red-500 focus:border-red-500 focus:ring-red-100",
                    )}
                    maxLength={5}
                    onChange={(e) =>
                      setValue("expiryDate", formatExpiryDate(e.target.value))
                    }
                    data-card-field="expiryDate"
                  />
                  {errors.expiryDate && (
                    <p
                      id={`${CARD_FIELD_IDS.expiry}-error`}
                      className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600"
                      role="alert"
                    >
                      {errors.expiryDate.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor={CARD_FIELD_IDS.cvc}
                    className="mb-1.5 block text-xs font-medium text-[rgb(48,48,48)] sm:text-sm"
                  >
                    Security code
                  </label>
                  <Input
                    id={CARD_FIELD_IDS.cvc}
                    value={watch("cvc")}
                    placeholder="CVC*"
                    autoComplete="cc-csc"
                    inputMode="numeric"
                    className={cn(
                      "h-12 sm:h-14 rounded-lg border-gray-300 text-sm sm:text-base transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
                      errors.cvc &&
                        "border-red-500 focus:border-red-500 focus:ring-red-100",
                    )}
                    maxLength={4}
                    onChange={(e) =>
                      setValue("cvc", e.target.value.replace(/\D/g, ""))
                    }
                    data-card-field="cvc"
                  />
                  {errors.cvc && (
                    <p
                      id={`${CARD_FIELD_IDS.cvc}-error`}
                      className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600"
                      role="alert"
                    >
                      {errors.cvc.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Apple Pay Option */}
        {/* <div className="overflow-hidden rounded-lg border border-gray-200">
          <label
            className={cn(
              "flex cursor-pointer items-center justify-center gap-3 transition-colors",
              paymentMethod === "apple-pay" ? "bg-black" : "bg-black",
            )}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="apple-pay"
              checked={paymentMethod === "apple-pay"}
              onChange={() => setValue("paymentMethod", "apple-pay")}
              className="h-5 w-5 hidden"
            />
            <img alt="Apple Pay" src="/brandnetwork/apple-pay.svg" />
          </label>
          {paymentMethod === "apple-pay" && (
            <div className="border-t border-gray-200 p-3 sm:p-4 md:p-5">
              <div className="flex items-start gap-2 sm:gap-3">
                <img src="/offsite-img.png" alt="Secure payment" className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                <p className="text-xs sm:text-sm text-gray-700">
                  After clicking <b>"COMPLETE PURCHASE"</b>, you will be
                  redirected to Apple Pay to complete your purchase securely.
                </p>
              </div>
            </div>
          )}
        </div> */}

        <div className="rounded-lg border border-gray-200 p-3 sm:p-4 md:p-5">
          <p className="mb-2 text-sm font-semibold text-[rgb(48,48,48)]">
            Coupon Code
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={couponCode}
              onChange={(event) => onCouponCodeChange(event.target.value)}
              placeholder="Enter coupon code"
              className="h-10 rounded-lg border-gray-300 text-sm"
              disabled={isApplyingCoupon}
            />
            <button
              type="button"
              onClick={() => {
                void onApplyCoupon();
              }}
              disabled={isApplyingCoupon || !couponCode.trim()}
              className={cn(
                "h-10 rounded-lg px-3 text-xs font-semibold text-white transition-colors sm:px-4 sm:text-sm",
                isApplyingCoupon || !couponCode.trim()
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-[rgb(48,48,48)] hover:bg-[rgb(28,28,28)]",
              )}
            >
              {isApplyingCoupon ? "Applying..." : "Apply"}
            </button>
          </div>

          {couponError && (
            <p className="mt-2 text-xs text-red-600 sm:text-sm">{couponError}</p>
          )}

          {appliedCouponLabel && !couponError && (
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-[rgb(44,158,44)] sm:text-sm">
                {appliedCouponLabel}
              </p>
              {canRemoveCoupon && (
                <button
                  type="button"
                  onClick={() => {
                    void onRemoveCoupon();
                  }}
                  disabled={isApplyingCoupon}
                  className={cn(
                    "text-xs font-semibold underline sm:text-sm",
                    isApplyingCoupon
                      ? "cursor-not-allowed text-gray-400"
                      : "text-[rgb(48,48,48)]",
                  )}
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
